// Импорт меню / тех-карт / склада / столов из iiko, r_keeper, Restik, Poster
// и любых CSV/XLSX. Файл разбирается на сервере (POST /import/analyze),
// пользователь видит превью и маппинг колонок, может переназначить их
// (повторный analyze с overrides), затем «Проверить» (dryRun) и «Импортировать».
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight,
  Table2, Package, UtensilsCrossed, BookOpen, Sparkles, X,
} from 'lucide-react';
import {
  analyzeImportFile, commitImport,
  type AnalyzeResult, type AnalyzeOverrides, type SheetInfo, type SheetEntity,
  type ImportField, type ImportSource, type CommitInput, type CommitResult,
} from '@/services/importer';

/* ─── labels ─────────────────────────────────────────────────────────────── */

const SOURCE_LABEL: Record<ImportSource, string> = {
  iiko: 'iiko',
  rkeeper: 'r_keeper',
  restik: 'Restik',
  poster: 'Poster',
  generic: 'Таблица',
};

const ENTITY_LABEL: Record<SheetEntity, string> = {
  menu: 'Меню',
  techcards: 'Тех-карты',
  stock: 'Склад',
  tables: 'Столы',
  unknown: 'Не распознано',
};

const ENTITY_ICON: Record<SheetEntity, typeof Table2> = {
  menu: UtensilsCrossed,
  techcards: BookOpen,
  stock: Package,
  tables: Table2,
  unknown: AlertTriangle,
};

const ENTITIES: SheetEntity[] = ['menu', 'techcards', 'stock', 'tables', 'unknown'];

/** Какие поля имеют смысл для типа листа — чтобы селект не пестрил лишним. */
const FIELDS_BY_ENTITY: Record<SheetEntity, ImportField[]> = {
  menu: ['name', 'category', 'price', 'cost', 'kind', 'section', 'weight', 'unit', 'description', 'sku', 'quantity'],
  techcards: ['dishName', 'ingredient', 'gross', 'net', 'unit', 'name'],
  stock: ['name', 'category', 'kind', 'unit', 'quantity', 'cost', 'sku', 'price'],
  tables: ['tableNumber', 'tableArea', 'seats', 'name'],
  unknown: [],
};

const IGNORE = '__ignore__';

/* Тона чипов сводки — отдельными константами, чтобы каждая строка классов
   была самосогласованной. */
const CHIP_TONE_OK = 'bg-emerald-50 text-emerald-700';
const CHIP_TONE_MUTED = 'bg-slate-100 text-slate-600';

type Include = { menu: boolean; techcards: boolean; stock: boolean; tables: boolean };

/* ─── component ──────────────────────────────────────────────────────────── */

type Step = 'pick' | 'review' | 'done';

export default function MenuImportTab() {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('pick');
  const [file, setFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<AnalyzeResult | null>(null);
  const [overrides, setOverrides] = useState<AnalyzeOverrides>({});
  const [include, setInclude] = useState<Include>({ menu: true, techcards: true, stock: true, tables: true });
  const [updateExisting, setUpdateExisting] = useState(true);
  const [createMissingIngredients, setCreateMissingIngredients] = useState(true);
  const [dryRunRes, setDryRunRes] = useState<CommitResult | null>(null);
  const [result, setResult] = useState<CommitResult | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => {
    setStep('pick'); setFile(null); setAnalysis(null); setOverrides({});
    setDryRunRes(null); setResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const analyze = useMutation({
    mutationFn: ({ f, ov }: { f: File; ov: AnalyzeOverrides }) => analyzeImportFile(f, ov),
    onSuccess: (data, { f, ov }) => {
      const first = !analysis;
      setFile(f);
      setAnalysis(data);
      setOverrides(ov);
      setDryRunRes(null);
      if (first) {
        setInclude({
          menu: data.dishes.length + data.modifiers.length > 0,
          techcards: data.techCards.length > 0,
          stock: data.stock.length > 0,
          tables: data.tables.length > 0,
        });
      }
      setStep('review');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'Не удалось разобрать файл')),
  });

  const onPick = (f: File | null | undefined) => {
    if (!f) return;
    if (!/\.(csv|xlsx|txt|tsv)$/i.test(f.name)) { toast.error('Поддерживаются CSV и XLSX'); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error('Файл больше 15 МБ'); return; }
    setAnalysis(null);
    analyze.mutate({ f, ov: {} });
  };

  // Переназначение — это повторный разбор файла на сервере с overrides.
  // mappingOverride заменяет маппинг листа целиком, поэтому шлём полный.
  const reanalyze = (ov: AnalyzeOverrides) => {
    if (!file) return;
    analyze.mutate({ f: file, ov });
  };

  const setSheetEntity = (sheetIndex: number, entity: SheetEntity) => {
    reanalyze({ ...overrides, entities: { ...(overrides.entities ?? {}), [String(sheetIndex)]: entity } });
  };

  const setSheetColumn = (sheet: SheetInfo, colIndex: number, field: ImportField | typeof IGNORE) => {
    const current: Record<string, ImportField> = { ...(overrides.mapping?.[String(sheet.index)] ?? sheet.mapping) };
    // одно поле — одна колонка: снимаем его с прежней
    for (const [k, v] of Object.entries(current)) if (v === field) delete current[k];
    if (field === IGNORE) delete current[String(colIndex)];
    else current[String(colIndex)] = field;
    reanalyze({ ...overrides, mapping: { ...(overrides.mapping ?? {}), [String(sheet.index)]: current } });
  };

  const buildInput = useCallback((dryRun: boolean): CommitInput => {
    const a = analysis!;
    const input: CommitInput = { dryRun, updateExisting, createMissingIngredients };
    if (include.menu) {
      input.dishes = a.dishes
        .filter((d) => d.priceRub != null)
        .map((d) => ({
          category: d.category, name: d.name, priceRub: d.priceRub as number,
          costRub: d.costRub ?? undefined, weight: d.weight, section: d.section, description: d.description,
        }));
      input.modifiers = a.modifiers.map((m) => ({ name: m.name, priceRub: m.priceRub ?? 0 }));
    }
    if (include.stock) {
      input.stock = a.stock.map((s) => ({
        name: s.name, kind: s.kind === 'prep' ? 'PREP' : 'GOODS', unit: s.unit,
        quantity: s.quantity, costRub: s.costRub, sku: s.sku,
      }));
    }
    if (include.techcards) {
      input.techCards = a.techCards
        .filter((t) => t.net != null && t.net > 0)
        .map((t) => ({ dishName: t.dishName, ingredient: t.ingredient, net: t.net as number, unit: t.unit }));
    }
    if (include.tables) {
      input.tables = a.tables.map((t) => ({ number: t.number, area: t.area, seats: t.seats }));
    }
    return input;
  }, [analysis, include, updateExisting, createMissingIngredients]);

  const dryRun = useMutation({
    mutationFn: () => commitImport(buildInput(true)),
    onSuccess: (r) => setDryRunRes(r),
    onError: (e: unknown) => toast.error(errMsg(e, 'Проверка не прошла')),
  });

  const commit = useMutation({
    mutationFn: () => commitImport(buildInput(false)),
    onSuccess: (r) => {
      setResult(r);
      setStep('done');
      toast.success('Импорт завершён');
      qc.invalidateQueries({ queryKey: ['menu'] });
      qc.invalidateQueries({ queryKey: ['categories'] });
      qc.invalidateQueries({ queryKey: ['dishes'] });
      qc.invalidateQueries({ queryKey: ['modifiers'] });
      qc.invalidateQueries({ queryKey: ['stock'] });
      qc.invalidateQueries({ queryKey: ['tables'] });
      qc.invalidateQueries({ queryKey: ['techcards'] });
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'Импорт не удался')),
  });

  const counts = useMemo(() => {
    if (!analysis) return null;
    return {
      dishes: analysis.dishes.length,
      modifiers: analysis.modifiers.length,
      categories: analysis.summary.categories.length,
      newCategories: analysis.summary.newCategories.length,
      stock: analysis.stock.length,
      techCards: analysis.techCards.length,
      tables: analysis.tables.length,
    };
  }, [analysis]);

  const anyIncluded = !!counts && (
    (include.menu && counts.dishes + counts.modifiers > 0)
    || (include.stock && counts.stock > 0)
    || (include.techcards && counts.techCards > 0)
    || (include.tables && counts.tables > 0)
  );

  /* ── step: pick ─────────────────────────────────────────────────────────── */
  if (step === 'pick') {
    return (
      <div className="grid gap-4">
        <div className="card p-4 grid gap-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-brand-50 text-brand-600"><Sparkles size={20} /></div>
            <div className="grid gap-1">
              <div className="font-medium">Импорт из iiko, r_keeper, Restik, Poster и любых таблиц</div>
              <div className="text-sm text-slate-600">
                Выгрузите из своей системы номенклатуру, тех-карты, остатки или столы в CSV / XLSX и загрузите
                файл сюда. Мы определим формат и предложим сопоставление колонок — перед записью всё можно проверить.
              </div>
            </div>
          </div>

          <label
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); onPick(e.dataTransfer.files?.[0]); }}
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 cursor-pointer transition-colors ${
              dragOver ? 'border-brand-400 bg-brand-50/50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.txt,.tsv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
            />
            {analyze.isPending ? (
              <>
                <Loader2 size={28} className="animate-spin text-brand-600" />
                <div className="text-sm text-slate-600">Разбираем файл…</div>
              </>
            ) : (
              <>
                <Upload size={28} className="text-slate-400" />
                <div className="text-sm font-medium">Перетащите файл или нажмите, чтобы выбрать</div>
                <div className="text-xs text-slate-500">CSV, XLSX · до 15 МБ · кодировка UTF-8 или Windows-1251</div>
              </>
            )}
          </label>
        </div>

        <div className="card p-4 grid gap-2 text-sm">
          <div className="font-medium">Что можно перенести</div>
          <ul className="grid gap-1.5 text-slate-600 sm:grid-cols-2">
            <li className="flex gap-2"><UtensilsCrossed size={16} className="mt-0.5 shrink-0 text-slate-400" /> Меню: категории, блюда, цены, описания, модификаторы</li>
            <li className="flex gap-2"><BookOpen size={16} className="mt-0.5 shrink-0 text-slate-400" /> Тех-карты: состав блюд с брутто/нетто</li>
            <li className="flex gap-2"><Package size={16} className="mt-0.5 shrink-0 text-slate-400" /> Склад: ингредиенты, единицы, остатки, себестоимость</li>
            <li className="flex gap-2"><Table2 size={16} className="mt-0.5 shrink-0 text-slate-400" /> Столы: номера, зоны, посадочные места</li>
          </ul>
          <div className="text-xs text-slate-500 pt-1">
            iiko: «Номенклатура → Экспорт в Excel» и «Технологические карты → Экспорт». r_keeper: отчёт «Меню» из StoreHouse / Manager.
            Restik и Poster: «Товары → Экспорт». Несколько листов в одном XLSX — нормально, каждый распознаётся отдельно.
          </div>
        </div>
      </div>
    );
  }

  /* ── step: done ─────────────────────────────────────────────────────────── */
  if (step === 'done' && result) {
    return (
      <div className="grid gap-4">
        <div className="card p-4 grid gap-3 border-emerald-200 bg-emerald-50">
          <div className="flex items-center gap-2 text-emerald-700 font-medium">
            <CheckCircle2 size={20} /> Импорт завершён
          </div>
          <ResultStats res={result} />
          <NotFoundNotes res={result} />
        </div>
        <div className="flex gap-2">
          <button className="btn-primary" onClick={reset}>Импортировать ещё файл</button>
        </div>
      </div>
    );
  }

  /* ── step: review ───────────────────────────────────────────────────────── */
  if (!analysis || !counts) return null;
  const a = analysis;
  const busy = analyze.isPending || dryRun.isPending || commit.isPending;

  return (
    <div className="grid gap-4">
      {/* header */}
      <div className="card p-4 flex flex-wrap items-center gap-3">
        <FileSpreadsheet size={22} className="text-brand-600 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">{a.file.name}</div>
          <div className="text-xs text-slate-500">
            Источник: <b className="text-slate-700">{SOURCE_LABEL[a.source]}</b>
            {a.source === 'generic' ? ' (формат определён по заголовкам)' : ''}
            {' · '}{a.file.format.toUpperCase()}{a.file.encoding ? `, ${a.file.encoding}` : ''}
            {' · '}листов: {a.sheets.length}
          </div>
        </div>
        {analyze.isPending && <Loader2 size={16} className="animate-spin text-brand-600" />}
        <button className="btn-ghost text-sm" onClick={reset} disabled={busy}><X size={16} /> Другой файл</button>
      </div>

      {/* warnings */}
      {a.warnings.length > 0 && (
        <div className="card p-3 border-amber-200 bg-amber-50 grid gap-1">
          {a.warnings.map((w, i) => (
            <div key={i} className="flex gap-2 text-sm text-amber-800">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {w}
            </div>
          ))}
        </div>
      )}

      {/* sheets */}
      {a.sheets.map((s) => (
        <SheetCard
          key={s.index}
          sheet={s}
          disabled={busy}
          onEntity={(entity) => setSheetEntity(s.index, entity)}
          onColumn={(col, field) => setSheetColumn(s, col, field)}
        />
      ))}

      {/* skipped */}
      {a.skipped.length > 0 && <SkippedCard rows={a.skipped} sheets={a.sheets} />}

      {/* summary + options */}
      <div className="card p-4 grid gap-4">
        <div className="grid gap-2">
          <div className="font-medium">Будет импортировано</div>
          <div className="flex flex-wrap gap-2 text-sm">
            <SummaryChip on={include.menu} count={counts.dishes} label="блюд" hint={`${a.summary.dishesNew} новых, ${a.summary.dishesExisting} уже есть`} />
            <SummaryChip on={include.menu} count={counts.modifiers} label="модификаторов" hint={`${a.summary.modifiersNew} новых`} />
            <SummaryChip on={include.menu} count={counts.categories} label="категорий" hint={`${counts.newCategories} новых`} />
            <SummaryChip on={include.techcards} count={counts.techCards} label="строк тех-карт" />
            <SummaryChip on={include.stock} count={counts.stock} label="позиций склада" hint={`${a.summary.stockNew} новых, ${a.summary.stockExisting} уже есть`} />
            <SummaryChip on={include.tables} count={counts.tables} label="столов" hint={`${a.summary.tablesNew} новых`} />
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          {(['menu', 'techcards', 'stock', 'tables'] as Array<keyof Include>).map((ent) => {
            const n = ent === 'menu' ? counts.dishes + counts.modifiers
              : ent === 'techcards' ? counts.techCards
              : ent === 'stock' ? counts.stock
              : counts.tables;
            const Icon = ENTITY_ICON[ent];
            return (
              <label key={ent} className={`flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 cursor-pointer ${n === 0 ? 'opacity-50' : ''}`}>
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand-600"
                  disabled={n === 0}
                  checked={include[ent] && n > 0}
                  onChange={(e) => { setInclude((p) => ({ ...p, [ent]: e.target.checked })); setDryRunRes(null); }}
                />
                <Icon size={16} className="text-slate-500" />
                <span className="text-sm">{ENTITY_LABEL[ent]}</span>
                <span className="ml-auto text-xs text-slate-500">{n}</span>
              </label>
            );
          })}
        </div>

        <div className="grid gap-2 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" className="h-4 w-4 accent-brand-600" checked={updateExisting}
              onChange={(e) => { setUpdateExisting(e.target.checked); setDryRunRes(null); }} />
            Обновлять существующие позиции (цены, описания, остатки — по совпадению названия)
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" className="h-4 w-4 accent-brand-600" checked={createMissingIngredients}
              onChange={(e) => { setCreateMissingIngredients(e.target.checked); setDryRunRes(null); }} />
            Создавать недостающие ингредиенты на складе для тех-карт
            {a.summary.techCardIngredientsMissing.length > 0 && (
              <span className="text-xs text-slate-500">({a.summary.techCardIngredientsMissing.length} шт.)</span>
            )}
          </label>
          {a.summary.techCardDishesMissing.length > 0 && include.techcards && (
            <div className="flex gap-2 text-xs text-amber-800">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>
                Тех-карты на блюда, которых нет ни в меню, ни в этом файле — будут пропущены:{' '}
                {a.summary.techCardDishesMissing.slice(0, 8).join(', ')}
                {a.summary.techCardDishesMissing.length > 8 ? ` и ещё ${a.summary.techCardDishesMissing.length - 8}` : ''}
              </span>
            </div>
          )}
        </div>

        {dryRunRes && <DryRunCard res={dryRunRes} />}

        <div className="flex flex-wrap gap-2 items-center">
          <button className="btn-outline" disabled={!anyIncluded || busy} onClick={() => dryRun.mutate()}>
            {dryRun.isPending ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            Проверить без записи
          </button>
          <button
            className="btn-primary"
            disabled={!anyIncluded || busy}
            onClick={() => {
              const q = 'Импортировать данные в заведение? Существующие позиции'
                + (updateExisting ? ' будут обновлены.' : ' останутся без изменений.');
              if (!confirm(q)) return;
              commit.mutate();
            }}
          >
            {commit.isPending ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            Импортировать
          </button>
          {!anyIncluded && <span className="text-xs text-slate-500">Выберите хотя бы один раздел с данными</span>}
        </div>
      </div>
    </div>
  );
}

/* ─── sub-components ─────────────────────────────────────────────────────── */

function SheetCard({
  sheet, disabled, onEntity, onColumn,
}: {
  sheet: SheetInfo;
  disabled: boolean;
  onEntity: (e: SheetEntity) => void;
  onColumn: (col: number, f: ImportField | typeof IGNORE) => void;
}) {
  const [open, setOpen] = useState(sheet.entity === 'unknown' || sheet.rowsTotal > 0);
  const entity = sheet.entity;
  const Icon = ENTITY_ICON[entity];
  const allowed = FIELDS_BY_ENTITY[entity];
  const mappedCount = Object.keys(sheet.mapping).length;
  const FIELD_TITLE = useMemo(() => Object.fromEntries(FIELD_LIST.map((f) => [f.id, f.title])) as Record<ImportField, string>, []);

  // Показать все поля, если для типа листа список пустой (unknown) —
  // пользователь сам решает, что там.
  const options: ImportField[] = allowed.length ? allowed : FIELD_LIST.map((f) => f.id);

  return (
    <div className="card overflow-hidden">
      <div className="w-full flex items-center gap-3 px-4 py-3">
        <button type="button" className="flex items-center gap-3 min-w-0 flex-1 text-left" onClick={() => setOpen((v) => !v)}>
          {open ? <ChevronDown size={16} className="text-slate-400 shrink-0" /> : <ChevronRight size={16} className="text-slate-400 shrink-0" />}
          <Icon size={18} className={entity === 'unknown' ? 'text-amber-500 shrink-0' : 'text-brand-600 shrink-0'} />
          <div className="min-w-0">
            <div className="font-medium truncate">{sheet.name}</div>
            <div className="text-xs text-slate-500">
              {ENTITY_LABEL[entity]} · строк: {sheet.rowsTotal} · колонок: {sheet.headers.length} · распознано: {mappedCount}
              {' · '}заголовок в строке {sheet.headerRow + 1}
            </div>
          </div>
        </button>
        <select
          className="input text-sm w-40 shrink-0"
          value={entity}
          disabled={disabled}
          onChange={(e) => onEntity(e.target.value as SheetEntity)}
          aria-label="Тип данных на листе"
        >
          {ENTITIES.map((e) => <option key={e} value={e}>{ENTITY_LABEL[e]}</option>)}
        </select>
      </div>

      {open && (
        <div className="border-t border-slate-100">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs text-slate-500">
                  <th className="px-3 py-2 font-medium whitespace-nowrap">Колонка в файле</th>
                  <th className="px-3 py-2 font-medium whitespace-nowrap">Импортировать как</th>
                  <th className="px-3 py-2 font-medium">Пример значений</th>
                </tr>
              </thead>
              <tbody>
                {sheet.headers.map((header, col) => {
                  const mapped = sheet.mapping[String(col)];
                  const samples = sheet.sample.map((r) => r[col]).filter((v) => v != null && String(v).trim() !== '').slice(0, 3);
                  return (
                    <tr key={col} className="border-t border-slate-100">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-slate-400 text-xs mr-2">#{col + 1}</span>
                        {header?.trim() ? header : <i className="text-slate-400">без названия</i>}
                      </td>
                      <td className="px-3 py-2">
                        <select
                          className={`input text-sm w-56 ${mapped ? '' : 'text-slate-400'}`}
                          value={mapped ?? IGNORE}
                          disabled={disabled}
                          onChange={(e) => onColumn(col, e.target.value as ImportField | typeof IGNORE)}
                        >
                          <option value={IGNORE}>— пропустить —</option>
                          {options.map((f) => <option key={f} value={f}>{FIELD_TITLE[f] ?? f}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        <div className="flex flex-wrap gap-1.5">
                          {samples.map((s, i) => (
                            <span key={i} className="rounded bg-slate-100 px-1.5 py-0.5 text-xs max-w-[220px] truncate">{String(s)}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SkippedCard({ rows, sheets }: { rows: AnalyzeResult['skipped']; sheets: SheetInfo[] }) {
  const [open, setOpen] = useState(false);
  const sheetName = (i: number) => sheets.find((s) => s.index === i)?.name ?? `лист ${i + 1}`;
  return (
    <div className="card overflow-hidden">
      <button type="button" className="w-full flex items-center gap-2 px-4 py-3 text-left text-sm hover:bg-slate-50" onClick={() => setOpen((v) => !v)}>
        {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
        <span className="text-slate-700">Пропущено строк: <b>{rows.length}</b></span>
        <span className="text-xs text-slate-500">(нет цены, услуги, пустые названия)</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 max-h-64 overflow-y-auto">
          <table className="min-w-full text-xs">
            <tbody>
              {rows.slice(0, 200).map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 text-slate-400 whitespace-nowrap">{sheets.length > 1 ? `${sheetName(r.sheet)} · ` : ''}стр. {r.row}</td>
                  <td className="px-3 py-1.5 truncate max-w-[320px]">{r.name}</td>
                  <td className="px-3 py-1.5 text-slate-500">{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 200 && <div className="px-3 py-2 text-xs text-slate-500">…и ещё {rows.length - 200}</div>}
        </div>
      )}
    </div>
  );
}

function SummaryChip({ on, count, label, hint }: { on: boolean; count: number; label: string; hint?: string }) {
  const muted = !on || count === 0;
  return (
    <span
      title={hint}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${muted ? CHIP_TONE_MUTED : CHIP_TONE_OK}`}
    >
      <b>{count}</b> {label}
    </span>
  );
}

function DryRunCard({ res }: { res: CommitResult }) {
  const line = (label: string, parts: Array<[string, number]>) => {
    const nz = parts.filter(([, n]) => n > 0);
    if (!nz.length) return null;
    return (
      <div key={label} className="flex flex-wrap gap-x-3 text-sm text-emerald-900">
        <span className="text-emerald-800 w-32">{label}</span>
        {nz.map(([k, n]) => <span key={k}>{k}: <b>{n}</b></span>)}
      </div>
    );
  };
  return (
    <div className="card p-3 grid gap-1.5 border-emerald-200 bg-emerald-50">
      <div className="text-sm font-medium flex items-center gap-2 text-emerald-700">
        <CheckCircle2 size={16} /> Проверка прошла — при импорте будет:
      </div>
      {line('Категории', [['создано', res.categories.created]])}
      {line('Блюда', [['создано', res.dishes.created], ['обновлено', res.dishes.updated], ['пропущено', res.dishes.skipped]])}
      {line('Модификаторы', [['создано', res.modifiers.created], ['пропущено', res.modifiers.skipped]])}
      {line('Склад', [['создано', res.stock.created], ['обновлено', res.stock.updated], ['пропущено', res.stock.skipped]])}
      {line('Тех-карты', [['создано', res.techCards.created], ['обновлено', res.techCards.updated], ['новых ингредиентов', res.techCards.ingredientsCreated]])}
      {line('Столы', [['создано', res.tables.created], ['пропущено', res.tables.skipped]])}
      <NotFoundNotes res={res} />
    </div>
  );
}

function NotFoundNotes({ res }: { res: CommitResult }) {
  const d = res.techCards.dishesNotFound;
  const ing = res.techCards.ingredientsNotFound;
  if (!d.length && !ing.length) return null;
  return (
    <>
      {d.length > 0 && (
        <div className="text-xs text-amber-800">
          Не найдены блюда для тех-карт: {d.slice(0, 10).join(', ')}{d.length > 10 ? ` и ещё ${d.length - 10}` : ''}
        </div>
      )}
      {ing.length > 0 && (
        <div className="text-xs text-amber-800">
          Не найдены ингредиенты (создание выключено): {ing.slice(0, 10).join(', ')}{ing.length > 10 ? ` и ещё ${ing.length - 10}` : ''}
        </div>
      )}
    </>
  );
}

function ResultStats({ res }: { res: CommitResult }) {
  const items: Array<[string, number]> = [
    ['категорий', res.categories.created],
    ['блюд', res.dishes.created + res.dishes.updated],
    ['модификаторов', res.modifiers.created],
    ['позиций склада', res.stock.created + res.stock.updated],
    ['тех-карт', res.techCards.created + res.techCards.updated],
    ['столов', res.tables.created],
  ];
  return (
    <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
      {items.map(([label, n]) => (
        <div key={label} className="rounded-lg bg-white/70 px-3 py-2">
          <div className="text-xl font-semibold text-emerald-900">{n}</div>
          <div className="text-xs text-emerald-800">{label}</div>
        </div>
      ))}
    </div>
  );
}

/* ─── utils ──────────────────────────────────────────────────────────────── */

// Полный список полей с русскими названиями — дублирует FIELD_TITLES бэкенда,
// чтобы селекты работали и до/без ответа сервера.
const FIELD_LIST: Array<{ id: ImportField; title: string }> = [
  { id: 'name', title: 'Название' }, { id: 'category', title: 'Категория' }, { id: 'price', title: 'Цена' },
  { id: 'cost', title: 'Себестоимость' }, { id: 'weight', title: 'Выход / вес' }, { id: 'unit', title: 'Единица' },
  { id: 'kind', title: 'Тип позиции' }, { id: 'section', title: 'Цех' }, { id: 'description', title: 'Описание' },
  { id: 'sku', title: 'Артикул' }, { id: 'quantity', title: 'Остаток' },
  { id: 'dishName', title: 'Блюдо (тех-карта)' }, { id: 'ingredient', title: 'Ингредиент' },
  { id: 'gross', title: 'Брутто' }, { id: 'net', title: 'Нетто' },
  { id: 'tableNumber', title: 'Номер стола' }, { id: 'tableArea', title: 'Зона' }, { id: 'seats', title: 'Мест' },
];

function errMsg(e: unknown, fallback: string): string {
  const anyE = e as { response?: { data?: { message?: string; error?: string } }; message?: string };
  return anyE?.response?.data?.message || anyE?.response?.data?.error || anyE?.message || fallback;
}
